import React from 'react'
import { Link } from 'react-router-dom'

import { F0Box, F0Heading, F0Icon, F0Text } from '@factorialco/f0-react'
import { Bookmark, Calendar, UserProtected } from '@factorialco/f0-react/icons/app'

import formatCurrency from 'modules/core/lib/formatCurrency'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

export type MarketplaceProductCardProps = {
  image: string
  imageFit?: 'contain' | 'cover'
  title: string
  price: { amount: number; currency: string }
  originalPrice?: { amount: number; currency: string }
  monthlyPrice?: { amount: number; months: number; currency: string }
  badge?: { label: string }
  isSaved?: boolean
  onSaveToggle?: () => void
  onClick?: () => void
  link?: string
  showZeroTouchIcon?: boolean
}

export const MarketplaceProductCard: React.FC<MarketplaceProductCardProps> = ({
  image,
  imageFit = 'contain',
  title,
  price,
  originalPrice,
  monthlyPrice,
  badge,
  isSaved = false,
  onSaveToggle,
  onClick,
  link,
  showZeroTouchIcon = true,
}) => {
  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target
    if (target instanceof HTMLElement && target.closest('[data-save-button]')) return
    onClick?.()
  }

  const formattedPrice = formatCurrency(toCents(price.amount))
  const formattedOriginalPrice = originalPrice
    ? formatCurrency(toCents(originalPrice.amount))
    : null

  const cardContent = (
    <div
      className={`group rounded-md overflow-hidden border border-solid border-f1-border-secondary transition-all hover:shadow hover:border-f1-border ${
        onClick || link ? 'cursor-pointer' : ''
      }`}
      onClick={link ? undefined : handleCardClick}
    >
      <F0Box
        position="relative"
        padding="xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="52"
      >
        <img
          src={image}
          alt={title}
          className={`max-w-full max-h-40 ${imageFit === 'cover' ? 'object-cover' : 'object-contain'}`}
        />
        {showZeroTouchIcon && (
          <div
            className="absolute top-3 left-3 w-7 h-7 rounded-full border border-solid border-f1-border bg-white flex items-center justify-center"
            title={i18n.t('it_management.device_catalog.card.zero_touch_tooltip')}
          >
            <F0Icon icon={UserProtected} size="md" color="default" />
          </div>
        )}
        {onSaveToggle && (
          <button
            data-save-button
            aria-label={
              isSaved
                ? i18n.t('it_management.device_catalog.card.unsave_label')
                : i18n.t('it_management.device_catalog.card.save_label')
            }
            title={
              isSaved
                ? i18n.t('it_management.device_catalog.card.unsave_label')
                : i18n.t('it_management.device_catalog.card.save_label')
            }
            className={`absolute top-3 right-3 w-7 h-7 rounded-full border border-solid border-f1-border flex items-center justify-center p-0 cursor-pointer transition-all group-hover:opacity-100 ${
              isSaved ? 'bg-white opacity-100' : 'bg-white opacity-0'
            }`}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
              onSaveToggle()
            }}
          >
            <F0Icon icon={Bookmark} size="md" color="default" />
          </button>
        )}
      </F0Box>

      <F0Box padding="md" display="flex" flexDirection="column" gap="sm">
        <F0Heading variant="heading" content={title} ellipsis={2} />

        <F0Box display="flex" gap="xs" alignItems="baseline">
          {originalPrice ? (
            <>
              <div className="text-f1-foreground-critical">
                <F0Heading variant="heading" content={formattedPrice} />
              </div>
              <div className="line-through">
                <F0Text variant="description" content={formattedOriginalPrice ?? ''} />
              </div>
            </>
          ) : (
            <F0Heading variant="heading" content={formattedPrice} />
          )}
        </F0Box>

        {monthlyPrice && (
          <F0Text
            variant="small"
            content={i18n.t('it_management.device_catalog.card.monthly_price', {
              amount: formatCurrency(toCents(monthlyPrice.amount)),
              months: monthlyPrice.months,
            })}
          />
        )}

        {badge && (
          <F0Box display="flex" gap="xs" alignItems="center">
            <F0Icon icon={Calendar} size="md" color="default" />
            <F0Text variant="small" content={badge.label} />
          </F0Box>
        )}
      </F0Box>
    </div>
  )

  if (link) {
    return (
      <Link to={link} className="no-underline text-inherit">
        {cardContent}
      </Link>
    )
  }

  return cardContent
}
